/**
 * Les traitements de l'espace de travail — le moniteur du locataire.
 *
 * Les imports, préparations, audits et glossaires tournent en fond, à travers
 * TOUTES les affaires. Le tiroir par affaire ne montre que les siens ; ici on
 * les réunit — surtout ce qui a échoué et attend une relance. Groupés par
 * affaire, chacun renvoyant à la sienne d'un clic.
 *
 * **La relance suit le pouls.** On rafraîchit vite tant qu'un travail tourne
 * (3 s), lentement sinon (15 s), et jamais quand l'onglet est en arrière-plan.
 */
import { useMemo, useState } from "react";

import { api, ErreurApi } from "../api/client";
import { Chargement, Echec, Vide } from "../composants/etats";
import { Etat } from "../composants/etiquettes";
import { quand } from "../composants/formats";
import { TRAVAIL_EN_COURS, useDonnees, useRelance } from "../composants/donnees";
import { Lien, naviguer } from "../routeur";

interface TravailLocataire {
  job_id: string; affaire_id: string;
  affaire_reference: string; affaire_label: string | null;
  genre: string; etat: string; progression: number; tentatives: number;
  motif: string | null; detail: string | null;
  nom: string | null; categorie: string | null;
  depuis: string; modifie_le: string;
}

/** Le libellé humain d'un genre de traitement. */
const GENRES: Record<string, string> = {
  IMPORT: "Import", PROVISION: "Provision", REINDEX: "Réindexation",
  CONVERT: "Conversion", PREPARE: "Préparation", AUDIT: "Audit",
  GLOSSAIRE: "Glossaire",
};

export function Traitements() {
  const [actifs, poserActifs] = useState(true);
  const url = `/api/v1/traitements${actifs ? "?actifs=true" : ""}`;
  const data = useDonnees<{ donnees: TravailLocataire[] }>(
    () => api.lire(url), [url]);

  const liste = data.donnees?.donnees ?? [];
  const enCours = liste.filter((t) => TRAVAIL_EN_COURS.has(t.etat)).length;
  const echecs = liste.filter((t) => t.etat === "FAILED").length;

  // Vite tant que ça tourne, lentement sinon — et jamais en arrière-plan.
  useRelance(() => data.recharger(), enCours > 0 ? 3000 : 15000);

  async function relancer(job_id: string) {
    try {
      await api.ecrire(`/api/v1/traitements/${job_id}/relance`);
    } catch (exc) {
      if (!(exc instanceof ErreurApi)) throw exc;
    }
    data.recharger();
  }

  // Groupés par affaire, l'ordre suivant la première apparition (déjà triée
  // par récence côté serveur).
  const groupes = useMemo(() => {
    const m = new Map<string, { ref: string; label: string | null; jobs: TravailLocataire[] }>();
    for (const t of liste) {
      const g = m.get(t.affaire_id);
      if (g) g.jobs.push(t);
      else m.set(t.affaire_id, {
        ref: t.affaire_reference, label: t.affaire_label, jobs: [t],
      });
    }
    return [...m.entries()];
  }, [liste]);

  return (
    <div className="ecran">
      <div className="ecran__tete">
        <div className="rangee rangee--espacee rangee--bas">
          <div>
            <h1 className="ecran__titre">Traitements</h1>
            <div className="ecran__compte">
              {enCours > 0 ? `${enCours} en cours` : "Rien en cours"}
              {echecs > 0 && (
                <span className="trait-alerte"> · {echecs} en échec</span>
              )}
            </div>
          </div>
          <div className="segments" role="group" aria-label="Filtre">
            <button type="button" aria-pressed={actifs}
                    className={actifs ? "segments__actif" : ""}
                    onClick={() => poserActifs(true)}>À traiter</button>
            <button type="button" aria-pressed={!actifs}
                    className={!actifs ? "segments__actif" : ""}
                    onClick={() => poserActifs(false)}>Tout l'historique</button>
          </div>
        </div>
      </div>

      <div className="ecran__corps">
        {data.charge && !data.donnees ? (
          <Chargement lignes={4} />
        ) : data.erreur ? (
          <Echec erreur={data.erreur} reessayer={data.recharger} />
        ) : liste.length === 0 ? (
          <div className="carte">
            <Vide
              titre={actifs ? "Rien à traiter" : "Aucun traitement"}
              aide={actifs
                ? "Les imports, préparations, audits et glossaires en cours ou en échec apparaîtront ici."
                : "L'historique des traitements de fond apparaîtra ici."}
            />
          </div>
        ) : (
          <div className="pile">
            {groupes.map(([affaire_id, g]) => (
              <div className="carte" key={affaire_id}>
                <div className="carte__corps pile pile--serree">
                  <div className="rangee rangee--espacee">
                    <button type="button" className="trait-affaire"
                            onClick={() => naviguer(`/affaires/${affaire_id}`)}>
                      <span className="mono">{g.ref}</span>
                      <span className="tronque">
                        {g.label || <span className="faible">Sans intitulé</span>}
                      </span>
                    </button>
                    <span className="discret">{g.jobs.length} traitement{g.jobs.length > 1 ? "s" : ""}</span>
                  </div>

                  <div className="trait-liste">
                    {g.jobs.map((t) => (
                      <div key={t.job_id} className="trait-ligne">
                        <span className="trait-genre">
                          {t.nom || GENRES[t.genre] || t.genre}
                        </span>
                        {TRAVAIL_EN_COURS.has(t.etat) ? (
                          <div className="jauge" aria-label={`${t.progression} %`}>
                            <div className="jauge__part" style={{ width: `${t.progression}%` }} />
                          </div>
                        ) : <span />}
                        <span className="discret nombre">{quand(t.modifie_le)}</span>
                        <Etat genre="travail" valeur={t.etat} />
                        {t.etat === "FAILED" ? (
                          <button type="button" className="bouton bouton--discret"
                                  onClick={() => relancer(t.job_id)}>Relancer</button>
                        ) : <span />}
                        {t.motif && (
                          <div className="trait-motif avis avis--erreur">
                            <strong>{t.motif}</strong>
                            {t.detail && <span> — {t.detail}</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="ecran__pied">
        <Lien vers="/affaires" className="discret">← Affaires</Lien>
      </div>
    </div>
  );
}
